// src/pages/ManualCircuitEditor.jsx
// =============================================================================
// Editor MANUALE di creazione circuito su mappa satellitare (Leaflet + Esri).
// Flusso: ricerca luogo (Nominatim) → disegno bordo sx/dx (auto-chiusura) →
// genera centerline → cordoli + curve (mini-settori) + settori S1/S2/S3 + area
// box + traguardo → salvataggio (POST /api/circuits).
// Tutta la geometria nuova è additiva: il motore gara usa la centerline `sectors`.
// =============================================================================
import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, Polygon, CircleMarker, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './CreateCircuit.css';
import './ManualCircuitEditor.css';
import { SectorEditor } from '../components/circuit/editors';
import {
  IconBack, IconSearch, IconSave, IconTrash, IconUndo, IconCheck, IconFlag,
  IconBox, IconCorner, IconKerb, IconLayers, IconMapPin, IconTrack,
} from '../components/circuit/icons';
import {
  haversineMeters, nearestTrackIndex, deriveCenterline, shouldAutoClose, chaikinClosed,
} from '../components/circuit/geometry';

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);
const AUTO_CLOSE_M = 12;   // soglia auto-chiusura bordi (sx/dx)
const DEFAULT_CENTER = [44.6525, 10.7815];

/* ===== util ring rotation (per "imposta partenza") ===== */
const rotateRing = (arr, k) => {
  if (!Array.isArray(arr) || arr.length === 0) return arr || [];
  const n = arr.length;
  const kk = ((k % n) + n) % n;
  return [...arr.slice(kk), ...arr.slice(0, kk)];
};

/* Tratto di un bordo (loop chiuso) fra due indici, scegliendo l'arco più corto. */
const borderSlice = (border, a, b) => {
  const n = border.length;
  if (!n) return [];
  const fwd = [];
  for (let i = a, g = 0; g <= n; i = (i + 1) % n, g++) { fwd.push(border[i]); if (i === b) break; }
  const bwd = [];
  for (let i = a, g = 0; g <= n; i = (i - 1 + n) % n, g++) { bwd.push(border[i]); if (i === b) break; }
  return fwd.length <= bwd.length ? fwd : bwd;
};

/* ===== sub-components mappa ===== */
function MapClickCapture({ onClick }) {
  useMapEvents({ click(e) { onClick({ lat: e.latlng.lat, lon: e.latlng.lng }); } });
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    if (target.bbox) {
      const [s, n, w, e] = target.bbox; // Nominatim: [south, north, west, east]
      map.fitBounds([[+s, +w], [+n, +e]], { padding: [40, 40] });
    } else if (target.lat != null) {
      map.setView([target.lat, target.lon], 17, { animate: true });
    }
  }, [target, map]);
  return null;
}

const labelIcon = (text) => L.divIcon({
  className: 'mce-corner-label',
  html: `<span>${String(text).replace(/</g, '&lt;')}</span>`,
  iconSize: [0, 0],
});

const TOOLS = [
  { id: 'left', label: 'Bordo SX', icon: IconTrack, color: '#06b6d4', hint: 'Clicca sulla mappa per tracciare il bordo SINISTRO. Si chiude da solo vicino al punto iniziale.' },
  { id: 'right', label: 'Bordo DX', icon: IconTrack, color: '#ec4899', hint: 'Clicca per tracciare il bordo DESTRO. Si chiude da solo vicino al punto iniziale.' },
  { id: 'kerb', label: 'Cordoli', icon: IconKerb, color: '#ef4444', hint: 'Clicca inizio e fine del cordolo sulla stessa linea perimetrale (sx o dx): il cordolo combacia esattamente col bordo.' },
  { id: 'corner', label: 'Curve', icon: IconCorner, color: '#C0FF03', hint: 'Clicca inizio e fine di una curva sulla centerline: diventa un mini-settore cronometrato.' },
  { id: 'box', label: 'Area Box', icon: IconBox, color: '#f59e0b', hint: 'Clicca i vertici del poligono dei box.' },
  { id: 'finish', label: 'Traguardo', icon: IconFlag, color: '#ffffff', hint: 'Clicca vicino alla centerline per posizionare la linea del via, poi "Imposta partenza".' },
];

export default function ManualCircuitEditor() {
  const navigate = useNavigate();
  const { id: editId } = useParams(); // presente solo in modalità modifica
  const isEdit = !!editId;
  const [loadingExisting, setLoadingExisting] = useState(isEdit);

  // ricerca
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const [located, setLocated] = useState(false);
  const searchAbort = useRef(null);

  // geometria
  const [tool, setTool] = useState('left');
  const [leftBorder, setLeftBorder] = useState([]);
  const [rightBorder, setRightBorder] = useState([]);
  const [leftClosed, setLeftClosed] = useState(false);
  const [rightClosed, setRightClosed] = useState(false);

  const [centerline, setCenterline] = useState(null); // { sectors, widthProfile, lengthMeters, widthMeters }
  const [kerbs, setKerbs] = useState([]);             // [{id, side, points:[{lat,lon}]}]
  const [kerbStart, setKerbStart] = useState(null);   // {side, idx} inizio cordolo sul bordo
  const [corners, setCorners] = useState([]);         // [{id,name,startIdx,endIdx}]
  const [cornerStart, setCornerStart] = useState(null); // idx temporaneo
  const [customSectors, setCustomSectors] = useState([]); // S1/S2/S3 visivi
  const [pitboxArea, setPitboxArea] = useState([]);
  const [startFinishIndex, setStartFinishIndex] = useState(0);
  // Config linea traguardo per il conteggio giri (vuoto = auto dai bordi).
  const [finishWidth, setFinishWidth] = useState('');   // larghezza totale in metri
  const [finishReverse, setFinishReverse] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sharedReadOnly, setSharedReadOnly] = useState(false);   // circuito della libreria condivisa

  const sectors = useMemo(() => centerline?.sectors || [], [centerline]);
  const sectorsLatLng = useMemo(() => sectors.map(s => [s.lat, s.lon]), [sectors]);

  // Anteprima visiva della linea del traguardo + freccia senso di marcia.
  // Stessa geometria del backend (buildFinishLine): direzione mediata sui primi
  // metri, semi-larghezza dall'input o dai bordi, inversione opzionale.
  const finishPreview = useMemo(() => {
    const s = sectors;
    if (!Array.isArray(s) || s.length < 2) return null;
    const lat0 = s[0].lat, lon0 = s[0].lon;
    const mPerLat = 111320, mPerLon = 111320 * Math.cos(lat0 * Math.PI / 180);
    // Direzione "avanti" mediata su ~8 m.
    let dx = 0, dy = 0, acc = 0;
    for (let i = 1; i < s.length && acc < 8 && i <= 12; i++) {
      dx = (s[i].lon - lon0) * mPerLon; dy = (s[i].lat - lat0) * mPerLat;
      acc += Math.hypot((s[i].lon - s[i - 1].lon) * mPerLon, (s[i].lat - s[i - 1].lat) * mPerLat);
    }
    let dl = Math.hypot(dx, dy);
    if (dl < 1e-6) { dx = (s[1].lon - lon0) * mPerLon; dy = (s[1].lat - lat0) * mPerLat; dl = Math.hypot(dx, dy) || 1; }
    dx /= dl; dy /= dl;
    if (finishReverse) { dx = -dx; dy = -dy; }
    const nx = -dy, ny = dx;
    // Semi-larghezza: input utente oppure auto dai bordi (default 15), clamp 8..60.
    const nearestLateral = (arr) => {
      if (!Array.isArray(arr) || !arr.length) return null;
      let best = Infinity, latv = null;
      for (const p of arr) {
        const x = (p.lon - lon0) * mPerLon, y = (p.lat - lat0) * mPerLat;
        const u = x * dx + y * dy, v = x * nx + y * ny;
        if (Math.abs(u) < best) { best = Math.abs(u); latv = Math.abs(v); }
      }
      return latv;
    };
    let half;
    const w = Number(finishWidth);
    if (w > 0) half = w / 2;
    else {
      const lv = nearestLateral(leftBorder), rv = nearestLateral(rightBorder);
      half = 15;
      if (lv != null && rv != null) half = Math.max(lv, rv) + 6;
      else if (lv != null) half = lv + 6;
      else if (rv != null) half = rv + 6;
    }
    half = Math.min(Math.max(half, 8), 60);

    const toLL = (mx, my) => [lat0 + my / mPerLat, lon0 + mx / mPerLon];
    // Linea trasversale (larghezza)
    const lineA = toLL(nx * half, ny * half);
    const lineB = toLL(-nx * half, -ny * half);
    // Freccia senso di marcia: asta dal centro in avanti + punta a V
    const L = Math.max(half * 1.0, 10);
    const tip = toLL(dx * L, dy * L);
    const hLen = Math.max(half * 0.4, 5), hWide = Math.max(half * 0.32, 4);
    const headL = toLL(dx * (L - hLen) + nx * hWide, dy * (L - hLen) + ny * hWide);
    const headR = toLL(dx * (L - hLen) - nx * hWide, dy * (L - hLen) - ny * hWide);
    return {
      width: Math.round(half * 2),
      line: [lineA, lineB],
      shaft: [[lat0, lon0], tip],
      head: [headL, tip, headR],
    };
  }, [sectors, finishWidth, finishReverse, leftBorder, rightBorder]);

  /* ===== modalità modifica: carica il circuito esistente ===== */
  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/circuits/${encodeURIComponent(editId)}`);
        if (!r.ok) throw new Error('Circuito non trovato');
        const c = await r.json();
        if (!alive) return;
        setName(c.name || '');
        const secs = Array.isArray(c.sectors)
          ? c.sectors.map((s, i) => ({ idx: i, lat: s.lat, lon: s.lon, dist: s.dist ?? 0 }))
          : [];
        if (secs.length >= 2) {
          setCenterline({
            sectors: secs,
            widthProfile: Array.isArray(c.widthProfile) && c.widthProfile.length ? c.widthProfile : secs.map(() => (c.params?.widthMeters || 6) / 2),
            lengthMeters: c.stats?.lengthMeters || (secs[secs.length - 1]?.dist || 0),
            widthMeters: c.params?.widthMeters || 6,
          });
        }
        if (Array.isArray(c.leftBorder) && c.leftBorder.length > 2) { setLeftBorder(c.leftBorder); setLeftClosed(true); }
        if (Array.isArray(c.rightBorder) && c.rightBorder.length > 2) { setRightBorder(c.rightBorder); setRightClosed(true); }
        setKerbs(Array.isArray(c.kerbs) ? c.kerbs : []);
        setCorners(Array.isArray(c.corners) ? c.corners : []);
        setCustomSectors(Array.isArray(c.customSectors) ? c.customSectors : []);
        setPitboxArea(Array.isArray(c.pitboxArea) ? c.pitboxArea : []);
        if (c.finishLine && typeof c.finishLine === 'object') {
          setFinishWidth(c.finishLine.halfWidth ? String(Math.round(c.finishLine.halfWidth * 2)) : '');
          setFinishReverse(!!c.finishLine.reverse);
        }
        if (secs.length) {
          const lats = secs.map(s => s.lat), lons = secs.map(s => s.lon);
          setFlyTarget({ bbox: [Math.min(...lats), Math.max(...lats), Math.min(...lons), Math.max(...lons)] });
        }
        setLocated(true);
        setTool('corner');
        // Libreria condivisa: si può guardare, non salvare.
        if (c.readOnly) {
          setSharedReadOnly(true);
          setError('Circuito della libreria condivisa: puoi consultarlo, ma non modificarlo. Per una versione tua crea un nuovo circuito.');
        }
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoadingExisting(false);
      }
    })();
    return () => { alive = false; };
  }, [isEdit, editId]);

  /* ===== ricerca Nominatim (debounced) ===== */
  useEffect(() => {
    if (!query.trim() || query.trim().length < 3) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        if (searchAbort.current) searchAbort.current.abort();
        searchAbort.current = new AbortController();
        setSearching(true);
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&q=${encodeURIComponent(query.trim())}`;
        const r = await fetch(url, { signal: searchAbort.current.signal, headers: { 'Accept-Language': 'it' } });
        const j = await r.json();
        setResults(Array.isArray(j) ? j : []);
      } catch (e) {
        if (e.name !== 'AbortError') setResults([]);
      } finally {
        setSearching(false);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [query]);

  const pickResult = (res) => {
    setFlyTarget({ bbox: res.boundingbox, lat: +res.lat, lon: +res.lon });
    setLocated(true);
    setResults([]);
    setQuery(res.display_name?.split(',')[0] || query);
    if (!name.trim()) setName(res.display_name?.split(',')[0] || '');
  };

  /* ===== gestione click mappa in base al tool ===== */
  const handleMapClick = useCallback((pt) => {
    if (tool === 'left') {
      if (leftClosed) return;
      setLeftBorder(prev => {
        const next = [...prev, pt];
        if (shouldAutoClose(next, AUTO_CLOSE_M)) { setLeftClosed(true); return [...prev]; }
        return next;
      });
    } else if (tool === 'right') {
      if (rightClosed) return;
      setRightBorder(prev => {
        const next = [...prev, pt];
        if (shouldAutoClose(next, AUTO_CLOSE_M)) { setRightClosed(true); return [...prev]; }
        return next;
      });
    } else if (tool === 'kerb') {
      // Cordolo = tratto di un BORDO: clicca inizio e fine sulla stessa linea
      // perimetrale; il cordolo combacia esattamente col bordo.
      const hasL = leftBorder.length > 1, hasR = rightBorder.length > 1;
      if (!hasL && !hasR) { setError('Disegna prima i bordi del circuito.'); return; }
      const li = hasL ? nearestTrackIndex(leftBorder, pt) : -1;
      const ri = hasR ? nearestTrackIndex(rightBorder, pt) : -1;
      const ld = li >= 0 ? haversineMeters(leftBorder[li], pt) : Infinity;
      const rd = ri >= 0 ? haversineMeters(rightBorder[ri], pt) : Infinity;
      if (!kerbStart) {
        const side = ld <= rd ? 'left' : 'right';
        setKerbStart({ side, idx: side === 'left' ? li : ri });
      } else {
        const side = kerbStart.side;
        const border = side === 'left' ? leftBorder : rightBorder;
        const endIdx = nearestTrackIndex(border, pt);
        const slice = borderSlice(border, kerbStart.idx, endIdx);
        if (slice.length >= 2) setKerbs(k => [...k, { id: Date.now(), closed: false, side, points: slice }]);
        setKerbStart(null);
      }
    } else if (tool === 'box') {
      setPitboxArea(prev => [...prev, pt]);
    } else if (tool === 'corner') {
      if (!sectors.length) { setError('Genera prima la centerline.'); return; }
      const idx = nearestTrackIndex(sectors, pt);
      if (cornerStart == null) {
        setCornerStart(idx);
      } else {
        const a = Math.min(cornerStart, idx);
        const b = Math.max(cornerStart, idx);
        setCorners(prev => [...prev, { id: Date.now(), name: `Curva ${prev.length + 1}`, startIdx: a, endIdx: b }]);
        setCornerStart(null);
      }
    } else if (tool === 'finish') {
      if (!sectors.length) { setError('Genera prima la centerline.'); return; }
      setStartFinishIndex(nearestTrackIndex(sectors, pt));
    }
  }, [tool, leftClosed, rightClosed, sectors, cornerStart, leftBorder, rightBorder, kerbStart]);

  /* ===== centerline =====
     Rigenerabile: se esisteva già una centerline (es. in modifica), le curve e
     i settori vengono riadattati per frazione invece di essere persi. */
  const generateCenterline = () => {
    setError('');
    const cl = deriveCenterline(leftBorder, rightBorder, { spacingMeters: 1 });
    if (!cl) { setError('Bordi insufficienti: chiudi entrambi i bordi (almeno 3 punti ciascuno).'); return; }
    const oldN = sectors.length;
    const newN = cl.sectors.length;
    const remap = (idx) => (oldN > 0 ? Math.min(newN - 1, Math.max(0, Math.round((idx / oldN) * newN))) : idx);
    setCenterline(cl);
    setCornerStart(null);
    setStartFinishIndex(0);
    setCorners(prev => prev.map(c => ({ ...c, startIdx: remap(c.startIdx), endIdx: remap(c.endIdx) })));
    setCustomSectors(prev => prev.map(s => ({ ...s, startIdx: remap(s.startIdx), endIdx: remap(s.endIdx) })));
  };

  /* ===== imposta partenza: ruota tutto all'origine scelta ===== */
  const applyStartFinish = () => {
    if (!sectors.length || startFinishIndex === 0) return;
    const k = startFinishIndex;
    const N = sectors.length;
    let dist = 0;
    const rotated = rotateRing(sectors, k).map((p, i, arr) => {
      if (i > 0) dist += haversineMeters(arr[i - 1], p);
      return { ...p, idx: i, dist };
    });
    const rotatedWidth = rotateRing(centerline.widthProfile || [], k);
    const remap = (idx) => ((idx - k) % N + N) % N;
    setCenterline({ ...centerline, sectors: rotated, widthProfile: rotatedWidth, lengthMeters: dist || centerline.lengthMeters });
    setCorners(prev => prev.map(c => ({ ...c, startIdx: remap(c.startIdx), endIdx: remap(c.endIdx) })));
    setCustomSectors(prev => prev.map(s => ({ ...s, startIdx: remap(s.startIdx), endIdx: remap(s.endIdx) })));
    setStartFinishIndex(0);
  };

  /* ===== azioni geometriche ===== */
  const undoBorder = (side) => {
    if (side === 'left') { setLeftClosed(false); setLeftBorder(p => p.slice(0, -1)); }
    else { setRightClosed(false); setRightBorder(p => p.slice(0, -1)); }
  };
  const clearBorder = (side) => {
    if (side === 'left') { setLeftBorder([]); setLeftClosed(false); }
    else { setRightBorder([]); setRightClosed(false); }
    setCenterline(null);
  };
  const cancelKerbStart = () => setKerbStart(null);

  /* ===== arrotonda i bordi disegnati a mano (smussa solo gli spigoli) ===== */
  const smoothBorders = () => {
    if (!leftClosed || !rightClosed) { setError('Chiudi entrambi i bordi prima di arrotondare.'); return; }
    const oldN = sectors.length;
    const nl = chaikinClosed(leftBorder, 2);
    const nr = chaikinClosed(rightBorder, 2);
    const cl = deriveCenterline(nl, nr, { spacingMeters: 1 });
    if (!cl) { setError('Impossibile arrotondare i bordi.'); return; }
    const newN = cl.sectors.length;
    const remap = (idx) => (oldN > 0 ? Math.min(newN - 1, Math.max(0, Math.round((idx / oldN) * newN))) : idx);
    setLeftBorder(nl);
    setRightBorder(nr);
    setCenterline(cl);
    setCorners(prev => prev.map(c => ({ ...c, startIdx: remap(c.startIdx), endIdx: remap(c.endIdx) })));
    setCustomSectors(prev => prev.map(s => ({ ...s, startIdx: remap(s.startIdx), endIdx: remap(s.endIdx) })));
    setError('');
  };
  const removeKerb = (id) => setKerbs(prev => prev.filter(k => k.id !== id));
  const removeCorner = (id) => setCorners(prev => prev.filter(c => c.id !== id));
  const renameCorner = (id, name) => setCorners(prev => prev.map(c => c.id === id ? { ...c, name } : c));

  /* ===== salvataggio ===== */
  const canSave = name.trim() && sectors.length >= 2 && !sharedReadOnly;
  const handleSave = async () => {
    if (!canSave) { setError('Serve un nome e una centerline generata.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        name: name.trim(),
        source: 'manual',
        leftBorder, rightBorder,
        sectors,
        pathPoints: sectors.map(s => ({ lat: s.lat, lon: s.lon })),
        widthProfile: centerline.widthProfile,
        kerbs,
        corners,
        customSectors,
        pitboxArea: pitboxArea.length >= 3 ? pitboxArea : [],
        params: { widthMeters: centerline.widthMeters, spacingMeters: 1, source: 'manual' },
        stats: { lengthMeters: centerline.lengthMeters },
        meta: { points: sectors.length, sectors: sectors.length },
        startFinish: { lat: sectors[0].lat, lon: sectors[0].lon, updatedAt: new Date().toISOString(), source: 'manual' },
        // Config linea traguardo: width>0 oppure reverse → invia, altrimenti reset (auto).
        finishLine: (Number(finishWidth) > 0 || finishReverse)
          ? { ...(Number(finishWidth) > 0 ? { width: Number(finishWidth) } : {}), reverse: finishReverse }
          : null,
      };
      const url = isEdit ? `${API_BASE}/api/circuits/${encodeURIComponent(editId)}` : `${API_BASE}/api/circuits`;
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || 'Errore salvataggio');
      navigate('/circuiti');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const activeTool = TOOLS.find(t => t.id === tool);

  return (
    <div className="mce-page">
      {/* HEADER */}
      <div className="mce-header">
        <button className="mce-back" onClick={() => navigate('/circuiti/crea')}>
          <IconBack size={18} /><span>Indietro</span>
        </button>
        <div className="mce-header-title">
          <IconMapPin size={18} />
          <input
            className="mce-name-input"
            placeholder="Nome del circuito..."
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>
        <button className={`mce-save-btn ${canSave ? '' : 'disabled'}`} onClick={handleSave} disabled={!canSave || saving}>
          {saving ? <span className="mce-spinner" /> : <IconSave size={18} />}
          <span>{saving ? 'Salvataggio...' : (isEdit ? 'Salva modifiche' : 'Salva circuito')}</span>
        </button>
      </div>

      <div className="mce-body">
        {/* SIDEBAR */}
        <aside className="mce-sidebar">
          {isEdit && (
            <section className="mce-section">
              <p className="mce-hint mce-hint--ok" style={{ marginTop: 0 }}>
                {loadingExisting ? 'Caricamento circuito…' : 'Modifica circuito esistente — al salvataggio aggiorni questo circuito.'}
              </p>
            </section>
          )}
          {/* Ricerca */}
          <section className="mce-section">
            <h3><IconSearch size={16} /> {isEdit ? 'Riposiziona la mappa' : 'Trova il circuito'}</h3>
            <div className="mce-search">
              <IconSearch size={16} />
              <input
                placeholder="Es. Misano, Autodromo Modena..."
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              {searching && <span className="mce-mini-spinner" />}
            </div>
            {results.length > 0 && (
              <div className="mce-search-results">
                {results.map(r => (
                  <button key={r.place_id} className="mce-search-item" onClick={() => pickResult(r)}>
                    <IconMapPin size={14} />
                    <span>{r.display_name}</span>
                  </button>
                ))}
              </div>
            )}
            {located && <p className="mce-hint mce-hint--ok"><IconCheck size={13} /> Posizione impostata. Usa gli strumenti per tracciare.</p>}
          </section>

          {/* Strumenti */}
          <section className="mce-section">
            <h3><IconTrack size={16} /> Strumenti</h3>
            <div className="mce-tools">
              {TOOLS.map(t => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    className={`mce-tool ${tool === t.id ? 'active' : ''}`}
                    style={{ '--tool-color': t.color }}
                    onClick={() => { setTool(t.id); setError(''); }}
                  >
                    <Icon size={18} />
                    <span>{t.label}</span>
                  </button>
                );
              })}
            </div>
            {activeTool && <p className="mce-hint">{activeTool.hint}</p>}
          </section>

          {/* Pannello contestuale per tool */}
          {(tool === 'left' || tool === 'right') && (
            <section className="mce-section">
              <h3><IconTrack size={16} /> Bordi</h3>
              <div className="mce-row">
                <span>Bordo SX</span>
                <strong style={{ color: '#06b6d4' }}>{leftBorder.length} pt {leftClosed ? '· chiuso' : ''}</strong>
              </div>
              <div className="mce-row">
                <span>Bordo DX</span>
                <strong style={{ color: '#ec4899' }}>{rightBorder.length} pt {rightClosed ? '· chiuso' : ''}</strong>
              </div>
              <div className="mce-btn-row">
                <button className="mce-mini-btn" onClick={() => undoBorder(tool)}><IconUndo size={14} /> Annulla</button>
                <button className="mce-mini-btn danger" onClick={() => clearBorder(tool)}><IconTrash size={14} /> Pulisci</button>
              </div>
              <button
                className="mce-action-btn"
                disabled={!leftClosed || !rightClosed}
                onClick={generateCenterline}
              >
                <IconCheck size={16} /> Genera centerline
              </button>
              {centerline && (
                <p className="mce-hint mce-hint--ok">
                  Centerline: {sectors.length} punti · {centerline.lengthMeters.toFixed(0)} m · larghezza media {centerline.widthMeters} m
                </p>
              )}
            </section>
          )}

          {tool === 'kerb' && (
            <section className="mce-section">
              <h3><IconKerb size={16} /> Cordoli</h3>
              {(!leftBorder.length && !rightBorder.length)
                ? <p className="mce-hint mce-hint--warn">Disegna prima i bordi del circuito.</p>
                : kerbStart
                  ? (
                    <>
                      <p className="mce-hint">Inizio impostato sul bordo <strong>{kerbStart.side === 'left' ? 'sinistro' : 'destro'}</strong> (punto {kerbStart.idx}). Clicca la <strong>fine</strong> sulla stessa linea.</p>
                      <button className="mce-mini-btn danger" onClick={cancelKerbStart}><IconUndo size={14} /> Annulla inizio</button>
                    </>
                  )
                  : <p className="mce-hint">Clicca l'<strong>inizio</strong> del cordolo su un bordo (sx o dx), poi la <strong>fine</strong> sulla stessa linea: il cordolo seguirà esattamente il perimetro.</p>
              }
              <div className="mce-list">
                {kerbs.map((k, i) => (
                  <div className="mce-list-item" key={k.id}>
                    <span><IconKerb size={13} /> Cordolo {i + 1} · {k.side === 'right' ? 'dx' : 'sx'} ({k.points.length})</span>
                    <button onClick={() => removeKerb(k.id)}><IconTrash size={13} /></button>
                  </div>
                ))}
                {kerbs.length === 0 && <p className="mce-empty">Nessun cordolo.</p>}
              </div>
            </section>
          )}

          {tool === 'corner' && (
            <section className="mce-section">
              <h3><IconCorner size={16} /> Curve (mini-settori)</h3>
              {!sectors.length && <p className="mce-hint mce-hint--warn">Genera prima la centerline.</p>}
              {cornerStart != null && <p className="mce-hint">Inizio impostato (punto {cornerStart}). Clicca la fine della curva.</p>}
              <div className="mce-list">
                {corners.map(c => (
                  <div className="mce-list-item mce-list-item--corner" key={c.id}>
                    <input value={c.name} onChange={e => renameCorner(c.id, e.target.value)} />
                    <span className="mce-corner-range">{c.startIdx}-{c.endIdx}</span>
                    <button onClick={() => removeCorner(c.id)}><IconTrash size={13} /></button>
                  </div>
                ))}
                {corners.length === 0 && <p className="mce-empty">Nessuna curva.</p>}
              </div>
              <button className="mce-action-btn" disabled={!leftClosed || !rightClosed} onClick={smoothBorders}>
                <IconCorner size={16} /> Arrotonda curve
              </button>
              <p className="mce-hint">Smussa gli spigoli dei bordi disegnati a mano: le curve diventano più morbide, i rettilinei restano invariati. I mini-settori vengono riadattati.</p>
            </section>
          )}

          {tool === 'box' && (
            <section className="mce-section">
              <h3><IconBox size={16} /> Area Box</h3>
              <div className="mce-row"><span>Vertici</span><strong>{pitboxArea.length}</strong></div>
              <div className="mce-btn-row">
                <button className="mce-mini-btn" onClick={() => setPitboxArea(p => p.slice(0, -1))}><IconUndo size={14} /> Annulla</button>
                <button className="mce-mini-btn danger" onClick={() => setPitboxArea([])}><IconTrash size={14} /> Pulisci</button>
              </div>
              {pitboxArea.length > 0 && pitboxArea.length < 3 && <p className="mce-hint mce-hint--warn">Servono almeno 3 punti.</p>}
            </section>
          )}

          {tool === 'finish' && (
            <section className="mce-section">
              <h3><IconFlag size={16} /> Traguardo</h3>
              {!sectors.length && <p className="mce-hint mce-hint--warn">Genera prima la centerline.</p>}
              <div className="mce-row"><span>Punto centerline</span><strong>{startFinishIndex}</strong></div>
              <button className="mce-action-btn" disabled={startFinishIndex === 0} onClick={applyStartFinish}>
                <IconFlag size={16} /> Imposta come partenza
              </button>
              <p className="mce-hint">Ruota il tracciato così che questo punto diventi lo start/finish.</p>

              <div className="mce-divider" />
              <h4 style={{ margin: '4px 0 8px', fontSize: '0.85rem', opacity: 0.9 }}>Conteggio giri</h4>
              <div className="mce-row">
                <span>Larghezza linea (m)</span>
                <input
                  type="number" min="0" max="120" step="1"
                  className="mce-input"
                  style={{ width: 80 }}
                  placeholder="auto"
                  value={finishWidth}
                  onChange={(e) => setFinishWidth(e.target.value)}
                />
              </div>
              <label className="mce-row" style={{ cursor: 'pointer' }}>
                <span>Inverti direzione di passaggio</span>
                <input type="checkbox" checked={finishReverse} onChange={(e) => setFinishReverse(e.target.checked)} />
              </label>
              {finishPreview && (
                <div className="mce-row">
                  <span>Larghezza effettiva</span>
                  <strong style={{ color: '#e10600' }}>{finishPreview.width} m</strong>
                </div>
              )}
              <p className="mce-hint">
                Lascia <strong>auto</strong> per usare i bordi pista. Aumenta la larghezza se qualche
                giro non viene rilevato; usa <strong>inverti</strong> se i giri non vengono contati affatto
                (tracciato disegnato nel verso opposto).
              </p>
            </section>
          )}

          {/* Settori S1/S2/S3 (sempre, una volta che c'è la centerline) */}
          {sectors.length > 0 && (
            <section className="mce-section">
              <h3><IconLayers size={16} /> Settori S1/S2/S3 (colori)</h3>
              <SectorEditor totalPoints={sectors.length} sectors={customSectors} onChange={setCustomSectors} />
            </section>
          )}

          {error && <div className="mce-error">{error}</div>}
        </aside>

        {/* MAPPA */}
        <div className="mce-map-wrap">
          <MapContainer className="mce-map" center={DEFAULT_CENTER} zoom={6} maxZoom={22} zoomControl scrollWheelZoom zoomSnap={0.25}>
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="Imagery &copy; Esri"
              maxNativeZoom={19}
              maxZoom={22}
            />
            <FlyTo target={flyTarget} />
            <MapClickCapture onClick={handleMapClick} />

            {/* Bordi */}
            {leftBorder.length > 1 && (
              <Polyline positions={leftBorder.map(p => [p.lat, p.lon])} pathOptions={{ color: '#06b6d4', weight: 3 }} />
            )}
            {leftClosed && leftBorder.length > 2 && (
              <Polyline positions={[[leftBorder[leftBorder.length - 1].lat, leftBorder[leftBorder.length - 1].lon], [leftBorder[0].lat, leftBorder[0].lon]]} pathOptions={{ color: '#06b6d4', weight: 3, dashArray: '4 4' }} />
            )}
            {rightBorder.length > 1 && (
              <Polyline positions={rightBorder.map(p => [p.lat, p.lon])} pathOptions={{ color: '#ec4899', weight: 3 }} />
            )}
            {rightClosed && rightBorder.length > 2 && (
              <Polyline positions={[[rightBorder[rightBorder.length - 1].lat, rightBorder[rightBorder.length - 1].lon], [rightBorder[0].lat, rightBorder[0].lon]]} pathOptions={{ color: '#ec4899', weight: 3, dashArray: '4 4' }} />
            )}
            {leftBorder[0] && <CircleMarker center={[leftBorder[0].lat, leftBorder[0].lon]} radius={5} pathOptions={{ color: '#fff', fillColor: '#06b6d4', fillOpacity: 1, weight: 2 }} />}
            {rightBorder[0] && <CircleMarker center={[rightBorder[0].lat, rightBorder[0].lon]} radius={5} pathOptions={{ color: '#fff', fillColor: '#ec4899', fillOpacity: 1, weight: 2 }} />}

            {/* Centerline */}
            {sectorsLatLng.length > 1 && (
              <Polyline positions={sectorsLatLng} pathOptions={{ color: '#e10600', weight: 2, opacity: 0.85, dashArray: '6 5' }} />
            )}

            {/* Settori colorati */}
            {customSectors.map(s => {
              const seg = sectors.slice(s.startIdx, s.endIdx + 1).map(p => [p.lat, p.lon]);
              return seg.length > 1 ? <Polyline key={s.id} positions={seg} pathOptions={{ color: s.color, weight: 6, opacity: 0.55 }} /> : null;
            })}

            {/* Curve (mini-settori) */}
            {corners.map(c => {
              const wrap = c.startIdx > c.endIdx;
              const idxs = wrap
                ? [...sectors.slice(c.startIdx), ...sectors.slice(0, c.endIdx + 1)]
                : sectors.slice(c.startIdx, c.endIdx + 1);
              const seg = idxs.map(p => [p.lat, p.lon]);
              const mid = idxs[Math.floor(idxs.length / 2)];
              return (
                <React.Fragment key={c.id}>
                  {seg.length > 1 && <Polyline positions={seg} pathOptions={{ color: '#C0FF03', weight: 7, opacity: 0.7 }} />}
                  {mid && <Marker position={[mid.lat, mid.lon]} icon={labelIcon(c.name)} interactive={false} />}
                </React.Fragment>
              );
            })}
            {cornerStart != null && sectors[cornerStart] && (
              <CircleMarker center={[sectors[cornerStart].lat, sectors[cornerStart].lon]} radius={6} pathOptions={{ color: '#C0FF03', fillColor: '#C0FF03', fillOpacity: 1, weight: 2 }} />
            )}

            {/* Cordoli (tratti del bordo perimetrale) */}
            {kerbs.map(k => (
              <Polyline key={k.id} positions={k.points.map(p => [p.lat, p.lon])} pathOptions={{ color: '#e10600', weight: 6, opacity: 0.95 }} />
            ))}
            {kerbStart && (() => {
              const b = kerbStart.side === 'left' ? leftBorder : rightBorder;
              const sp = b[kerbStart.idx];
              return sp ? <CircleMarker center={[sp.lat, sp.lon]} radius={6} pathOptions={{ color: '#fff', fillColor: '#e10600', fillOpacity: 1, weight: 2 }} /> : null;
            })()}

            {/* Area box */}
            {pitboxArea.length > 0 && (
              <Polygon positions={pitboxArea.map(p => [p.lat, p.lon])} pathOptions={{ color: '#f59e0b', fillColor: '#f59e0b', fillOpacity: 0.22, weight: 2, dashArray: pitboxArea.length < 3 ? '6 6' : undefined }} />
            )}

            {/* Traguardo */}
            {sectors[startFinishIndex] && (
              <CircleMarker center={[sectors[startFinishIndex].lat, sectors[startFinishIndex].lon]} radius={7} pathOptions={{ color: '#fff', fillColor: '#e10600', fillOpacity: 1, weight: 3 }} />
            )}

            {/* Anteprima linea traguardo + freccia senso di marcia (strumento Traguardo) */}
            {tool === 'finish' && finishPreview && (
              <>
                <Polyline positions={finishPreview.line} pathOptions={{ color: '#ffffff', weight: 5, opacity: 0.95 }} />
                <Polyline positions={finishPreview.line} pathOptions={{ color: '#e10600', weight: 2, dashArray: '6 6' }} />
                <Polyline positions={finishPreview.shaft} pathOptions={{ color: '#C0FF03', weight: 4, opacity: 0.95 }} />
                <Polyline positions={finishPreview.head} pathOptions={{ color: '#C0FF03', weight: 4, opacity: 0.95 }} />
              </>
            )}
          </MapContainer>

          <div className="mce-map-badge" style={{ '--tool-color': activeTool?.color }}>
            <span className="mce-map-badge-dot" />
            {activeTool?.label || 'Naviga'}
          </div>
        </div>
      </div>
    </div>
  );
}
